import React, { useState, useEffect, useCallback } from 'react';

import Button from 'src/components/controls/forms/redesign/FormButton';
import useFetch from 'use-http';
import { Url } from 'src/api';
import { useSelector, useDispatch } from 'react-redux';
import { IRootState } from 'src/reducers';
import CardContainer from 'src/components/common/CardContainer.redesign';
import TractHorizonSummary from 'src/models/tractHorizonSummary';
import TractCard from '../../TractComponents/TractCard2';
import { push } from 'connected-react-router';
import NewTract from 'src/models/tract_detail/NewTract';
import AddTract from '../../TractComponents/AddTract';
import PagedResultSet from 'src/models/common/PagedResultSet';
import County from 'src/models/County';

import actions from 'src/actions';
import { LandSystem } from 'src/models/LandSystem';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import DialogList from 'src/dialogs';
import Dialog from 'src/components/common/Dialog';
import SurfaceTract from 'src/models/SurfaceTract';
import SurfaceTractListCard from '../../TractComponents/SurfaceTractListCard';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import _ from 'lodash';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import Filter from 'src/components/common/Filter';
import SearchListData from 'src/models/SearchListData';
import { ITableConfig } from 'src/components/common/table';
import TractDetail from 'src/models/tract_detail/TractDetail';
import moment from 'moment';
import PagedItemList from 'src/components/common/PagedItemList';

interface ITractListProps {
    sectionNumber: number;
    landSystem: LandSystem;
    tractType: string;
    tagFilterUpdated?: boolean;
    resetTagFilterCallback?: () => void;
    refreshProjectTagsCallback?: () => void;
    reloadCallback?: () => void;
    getDataOptions?: () => ODataOptions;
    setDataOptions?: (options: ODataOptions) => void;
    searchFilterData?: SearchListData[];
    filterTracts?: boolean;
}

const TractList: React.FC<ITractListProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const [tracts, setTracts] = useState<PagedResultSet<TractHorizonSummary>>(new PagedResultSet<TractHorizonSummary>());
    const [surfaceTracts, setSurfaceTracts] = useState<SurfaceTract[]>([]);
    const [loading, setLoading] = useState(true);
    const [resetLastPage, setResetLastPage] = useState(false);
    const [filterTracts, setFilterTracts] = useState(true);
    const [counties, setCounties] = useState(undefined);
    const [searchFilterData, setSearchFilterData] = useState<Array<SearchListData>>([]);
    const [surfaceTractToDelete, setSurfaceTractToDelete] = useState<SurfaceTract>(undefined);
    const [mineralTractToDelete, setMineralTractToDelete] = useState<TractHorizonSummary>(undefined);
    const [mineralTractSurfaceOwnerships, setMineralTractSurfaceOwnerships] = useState<Array<SurfaceTract>>([]);
    const [selectedTractNumber, setSelectedTractNumber] = useState<number>(undefined);
    const [selectedSurfaceTractId, setSelectedSurfaceTractId] = useState<number>(undefined);
    const [displayDeleteMessage, setDisplayDeleteMessage] = useState<boolean>(false);
    const { get, post, response, request } = useFetch();
    const dispatch = useDispatch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    const [tractListDataOptions, setTractListDataOptions] = useState<ODataOptions>(undefined);

    const loadSurfaceTractsDelegate = (nextPage: boolean) => {
        props.refreshProjectTagsCallback();
        loadSurfaceTracts(tractListDataOptions.toString());
    };
/*
    const loadTractsDelegate = (nextPage: boolean) => {
        props.refreshProjectTagsCallback();
        loadTracts(tractListDataOptions.toString());
    };
*/
    const loadTractsPagingDelegate = async (dataOptions: ODataOptions, resetSearch: boolean) => {
        loadTracts(dataOptions.toString());
    };

    const loadTracts = async (filter: string) => {
        let data: PagedResultSet<TractHorizonSummary>;
        if (props.landSystem !== LandSystem.EastTexas) {
            data = await get(Url.tract.getTracts(appContext, props.sectionNumber, filter));
        } else {
            data = await get(Url.tract.getEastTexasTracts(appContext, 1, filter));
        }
        if (response.ok) {
            if ((tractListDataOptions?.getIsNextPage() !== undefined ? tractListDataOptions?.getIsNextPage() : false) === true) {
                let tempResult = _.cloneDeep(tracts);
                tempResult.pageNumber += 1;
                tempResult.items = [...tempResult?.items].concat([...(data?.items !== undefined ? data.items : [])]);
                setTracts(tempResult);
            } else {                
                setTracts(data);
            }
        } else {
            console.log('bad request', response);
        }
        setResetLastPage(false);
        setLoading(false);
    };

    const loadSurfaceTracts = async (filter: string) => {
        const data = await get(Url.tract.getSurfaceTracts(appContext, props.sectionNumber, filter));

        if (response.ok) {
            setSurfaceTracts(data);
        }
        setLoading(false);
        setResetLastPage(false);
    };

    const loadSurfaceTract = async (surfaceTractNumber: number) => {
        const result: SurfaceTract = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/1/tracts/surface-tracts/${surfaceTractNumber}`
        );

        if (response.ok) {
            if (result.recordTypeId) {
                result.recordDate = moment(result.recordDate).format('YYYY-MM-DD') as unknown as Date;
            }
            if (result.examinedHistories?.length > 0) {
                result.examinedHistories.forEach((eh) => {
                    eh.takeOffDate = moment(eh.takeOffDate).format('YYYY-MM-DD') as unknown as Date;
                    eh.examinedDate = moment(eh.examinedDate).format('YYYY-MM-DD') as unknown as Date;
                });
            }
            setSurfaceTractToDelete(result);
        }
    };

    const getCounties = async () => {
        let countiesData;
        if (props.landSystem !== LandSystem.EastTexas) {
            countiesData = await get(Url.project.getSectionCounties(appContext, props.sectionNumber));
        } else {
            countiesData = await get(Url.project.getProjectCounties(appContext));
        }

        if (response.ok) {
            const prs = new PagedResultSet<County>();
            prs.items = countiesData?.items;
            setCounties(prs);
            setLoading(false);
        }
    };

    const onNavigateFromDeletTractDialog = () => {
        dispatch(
            push(
                `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/tracts}`
            )
        );
        console.log('navigating from deleteTract dialog');
    };

    const onConfirmDeletTractDialog = () => {
        console.log('navigating from deleteTract dialog');
    };

    const onCloseDeletTractDialog = () => {
        props.reloadCallback();
        console.log('closing deleteTract dialog');
    };

    const showDeleteTractDialog = (horizons: Array<TractDetail>, surfaceOwnerships: Array<SurfaceTract>, tractType: number) => {
        dialogContext.tract.deleteTract(horizons, surfaceOwnerships, onNavigateFromDeletTractDialog, onConfirmDeletTractDialog, onCloseDeletTractDialog, onCloseDeletTractDialog, tractType);
    };

    const onDeleteTract = (tractNumber: number) => {
        let tract = tracts.items?.find((t) => t.tractNumber === tractNumber);
        let hasOwners = false;

        if (tract?.horizons && tract.horizons.length > 0) {
            tract.horizons.forEach((h) => {
                if (h.ownerCount > 0) {
                    hasOwners = true;
                }
            });
        }

        if (hasOwners) {
            console.log('Cannot delete tract', tractNumber);
            setDisplayDeleteMessage(true);
        } else {
            setMineralTractToDelete(tract);
            loadSurfaceOwnerships(tract.tractNumber);
        }
    };

    const onDeleteSurfaceTract = (surfaceTractId: number) => {
        let surfaceTract = surfaceTracts.find((st) => st.id === surfaceTractId);
        loadSurfaceTract(surfaceTract.number);
    };

    useEffect(() => {
        resetPagingOptions();
    }, []);

    useEffect(() => {
        if (tractListDataOptions !== undefined) {
            setLoading(true);
            if (props.tractType === 'Surface Tract') {
                loadSurfaceTracts(tractListDataOptions.toString());
            } else {
                loadTracts(tractListDataOptions.toString());
            }
            setResetLastPage(true);
            getCounties();
        }
    }, [tractListDataOptions]);

    useEffect(() => {
        console.log('tracts updated:' + tracts?.items?.length);
        props.reloadCallback();
    }, [tracts]);

    useEffect(() => {
        let newDataOptions = _.cloneDeep(tractListDataOptions);
        let hadFilters = newDataOptions !== undefined ? newDataOptions.hasFilters() : false;
        if (searchFilterData.length > 0) {
            newDataOptions?.clearFilters('KUDUTags');
            if (filterTracts === true) {
                for ( let tagListIndex = 0; tagListIndex < searchFilterData?.length; tagListIndex++) {
                    if (searchFilterData[tagListIndex].state === true) {
                        newDataOptions?.addFilter(new ODataFilter('KUDUTags', ODataFilterOperators.Equals, encodeURIComponent(searchFilterData[tagListIndex].tag.name)));
                    }
                }
                if (newDataOptions.hasFilters() || hadFilters) {
                    newDataOptions.setIsNextpage(false);
                    newDataOptions.addPaging(1, tractListDataOptions.getPageSize());
                    setTractListDataOptions(newDataOptions);
                }
            }
        }
    }, [searchFilterData]);

    useEffect(() => {
        if (filterTracts !== undefined) {
            let newDataOptions = _.cloneDeep(tractListDataOptions);
            let hadFilters = newDataOptions !== undefined ? newDataOptions.hasFilters() : false;
            newDataOptions?.clearFilters('KUDUTags');
            if (filterTracts === true) {
                for ( let tagListIndex = 0; tagListIndex < searchFilterData?.length; tagListIndex++) {
                    if (searchFilterData[tagListIndex].state === true) {
                        newDataOptions?.addFilter(new ODataFilter('KUDUTags', ODataFilterOperators.Equals, encodeURIComponent(searchFilterData[tagListIndex].tag.name)));
                    }
                }
            }
            if (newDataOptions?.hasFilters() || hadFilters) {
                newDataOptions.setIsNextpage(false);
                newDataOptions.addPaging(1, tractListDataOptions.getPageSize());
                setTractListDataOptions(newDataOptions);
            }
        }
    }, [filterTracts]);

    useEffect(() => {
        if (props.filterTracts !== undefined) {
            setFilterTracts(props.filterTracts);
        }
    }, [props.filterTracts]);

    useEffect(() => {
        if (props?.searchFilterData.length > 0) {
            setSearchFilterData(props.searchFilterData);
        }
    }, [props.searchFilterData]);

    useEffect(() => {
        if (props.sectionNumber && tractListDataOptions !== undefined) {
            let newDataOptions = _.cloneDeep(tractListDataOptions);
            newDataOptions.setIsNextpage(false);
            newDataOptions.addPaging(1, tractListDataOptions.getPageSize());
            setTractListDataOptions(newDataOptions);
        }
    }, [props.sectionNumber]);

    const JeffersonianTractSearchUpdate = useCallback(
        _.debounce((filter: string) => {
            if (filter) {
                if (props.tractType === 'Surface Tract') {
                    loadSurfaceTracts(filter);
                } else {
                    loadTracts(filter);
                }
                
                console.log('Getting Jeffersonian Tracts', filter);
            }
        }, 800),
        [props.sectionNumber]
    );

    useEffect(() => {
        if (surfaceTractToDelete !== undefined) {
            if (surfaceTractToDelete?.ownerships?.length > 0) {
                console.log('Cannot delete tract', surfaceTractToDelete.id);
                setDisplayDeleteMessage(true);
            } else if (surfaceTractToDelete?.mineralTracts?.length > 0) {
                showDeleteTractDialog(surfaceTractToDelete?.mineralTracts, undefined, 0);
            } else {
                console.log('Deleting Tract', surfaceTractToDelete?.id);
                setDisplayDeleteMessage(false);
                setSelectedSurfaceTractId(surfaceTractToDelete?.id);
                dispatch(actions.layout.showDialog(DialogList.CONFIRM_TRACT_DELETE));
            }
        }
    }, [surfaceTractToDelete]);

    useEffect(() => {
        if (mineralTractSurfaceOwnerships !== undefined && mineralTractSurfaceOwnerships.length > 0) {
            showDeleteTractDialog(undefined, mineralTractSurfaceOwnerships, 1);
        } else if (mineralTractToDelete !== undefined) {
            console.log('Deleting Tract', mineralTractToDelete?.tractNumber);
            setDisplayDeleteMessage(false);
            setSelectedTractNumber(mineralTractToDelete.tractNumber);
            dispatch(actions.layout.showDialog(DialogList.CONFIRM_TRACT_DELETE));
        }
    }, [mineralTractSurfaceOwnerships]);

    const loadSurfaceOwnerships = async (tractNumber: number) => {
        setLoading(true);
        console.log(tractNumber);
        let data = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${props.sectionNumber}/tracts/${tractNumber}/surfaceTracts`);

        if (response.ok) {
            if (data && data.length > 0) {
                data = data?.map((sp: SurfaceTract) => {
                    if (sp.recordTypeId) {
                        sp.recordDate = (moment(sp.recordDate).format('YYYY-MM-DD') as unknown as Date);
                    }
                    return sp;
                });
            }
            setMineralTractSurfaceOwnerships(data);
            setLoading(false);
        }
    };

    const eastTexasTractSearchUpdate = useCallback(
        _.debounce((filter: string) => {
            if (props.tractType === 'Surface Tract') {
                loadSurfaceTracts(filter);
            } else {
                loadTracts(filter);
            }
            console.log('Getting East Texas Tracts', filter);
        }, 800),
        []
    );
/*
    const updateEastTexasFilter = (options: ODataOptions) => {
        const stringOptions = options.toString();
        eastTexasTractSearchUpdate(stringOptions);
        console.log('New Filter Options', stringOptions);
    };
*/
    const updateTractFilter = (options: ODataOptions) => {
        let tempDataOptions = tractListDataOptions === undefined ? new ODataOptions() : tractListDataOptions;
        tempDataOptions.setIsNextpage(false);
        tempDataOptions.addPaging(1, tractListDataOptions?.getPageSize());
        let hadOmniFilters = tempDataOptions?.toString().search('omni') > 0 ? true : false;
        let hadCustomFilters = tempDataOptions?.toString().search('where') > 0 ? true : false;
        let hasOrderby = options?.toString().search('orderby') > 0 ? true : false;
        if (tempDataOptions?.clearFilters && (options.hasFilters() || hadOmniFilters || hadCustomFilters || hasOrderby)) {
            if (hadOmniFilters) {
                tempDataOptions?.clearFilters('omni');
            }
            if (hadCustomFilters) {
                tempDataOptions?.clearFilters('where');
            }
            if (tempDataOptions) {
                tempDataOptions.merge(options);
                const stringOptions = tempDataOptions.toString();
                if (stringOptions) {
                    if (props.landSystem === LandSystem.EastTexas) {
                        eastTexasTractSearchUpdate(stringOptions);
                    } else {
                        JeffersonianTractSearchUpdate(stringOptions);
                    }
                    setTractListDataOptions(tempDataOptions);
                    console.log('New Filter Options', stringOptions);
                }
            }
        }
    };

    const tractListContent = () => {
        console.log('tractListContent');
        return (
            <>
                {displayDeleteMessage && (
                    <div
                        style={{
                            padding: '2rem',
                            border: '2px red solid',
                            margin: '1rem',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.1em',
                        }}
                    >
                        Cannot delete a tract with owners
                    </div>
                )}
                {tracts && tracts?.items?.map((tract: TractHorizonSummary) => {
                    let tractActions: ContextMenuAction[] = [{ text: 'Delete Tract', action: () => onDeleteTract(tract.tractNumber) },
                                                             { text: 'Edit Tags', action: () => dialogContext.project.addUpdateProjectTags(
                                                                'edit', props.refreshProjectTagsCallback, tract.tractNumber, tract.id, tract.sectionNumber)}];
                    console.log('TractCard');

                    return (
                        <TractCard
                            key={tract.id.toString()}
                            tract={tract}
                            onClick={(event: React.MouseEvent<HTMLDivElement>, horizonNumber: number) => {
                                dispatch(
                                    push(
                                        `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${props.sectionNumber}/tracts/${tract.tractNumber}/horizons/${horizonNumber}`
                                    )
                                );
                            }}
                            actions={tractActions}
                        />
                    );
                })}
            </>
        );
    };

    const resetPagingOptions = () => {
        const tableConfig: ITableConfig = {
            rowCount: 20,
            idField: 'id',
            pageNumber: 1,
            columns: []
        };
        let newDataOptions = ODataOptions.from(tableConfig);
        newDataOptions.addSkipTop(0, 20);
        newDataOptions.addOrderBy('Number', ODataOrderByDirection.Ascending);
        setTractListDataOptions(newDataOptions);
        console.log('Reset state called!');
    };

    const tractsList = () => {
        if (!tracts || tracts.items?.length === 0) {
            return (
                <div style={{ padding: '2rem', border: '2px red solid', margin: '1rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1em' }}>
                    No Tracts Found For Section
                </div>
            );
        }

        return (
            <PagedItemList
                style={{ overflowY: 'scroll', maxHeight: '73vh' }}
                resultsLabelStyle={{ marginLeft: '1rem', marginRight: '2.1rem', background: 'gainsboro', border: '1px solid gainsboro', borderTopLeftRadius: '5px', borderTopRightRadius: '5px' }}
                resetLastPage={resetLastPage}
                ResetStateCallback={resetPagingOptions}
                dataOptions={tractListDataOptions}
                GetPagedItemList={loadTractsPagingDelegate}
                DisplayList={tractListContent}
                orderByColumn={tractListDataOptions.getOrderByProperty() !== undefined ?
                        tractListDataOptions.getOrderByProperty() : 'Number'}
                orderByDirection={tractListDataOptions.getOrderByDirection() !== undefined ?
                        tractListDataOptions.getOrderByDirection() : ODataOrderByDirection.Descending}
                startingPage={1}
                rowsPerPage={tracts.pageSize}
                totalRows={tracts.totalRecords}
            />
        );
    };

    const surfaceTractList = () => {
        // TODO: Get A proper number for the Surface Tract instead of using ID.
        if (!surfaceTracts || surfaceTracts.length === 0) {
            return (
                <div style={{ padding: '2rem', border: '2px red solid', margin: '1rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1em' }}>
                    No Surface Tracts Found For Section
                </div>
            );
        }
        if (loading === true) {
            return (
                <div style={{ padding: '2rem', border: '2px red solid', margin: '1rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1em' }}>
                    Loading Data...
                </div>
            );
        }

        return (
            <CardContainer>
                {displayDeleteMessage && (
                    <div
                        style={{
                            padding: '2rem',
                            border: '2px red solid',
                            margin: '1rem',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.1em',
                        }}
                    >
                        Cannot delete a surface tract with owners
                    </div>
                )}
                {surfaceTracts?.map((surfaceTract: SurfaceTract) => {
                    let surfaceTractActions: ContextMenuAction[] = [{ text: 'Delete Tract', action: () => onDeleteSurfaceTract(surfaceTract.id) },
                                                                    { text: 'Edit Tags', action: () => dialogContext.project.addUpdateProjectTags(
                                                                      'edit', loadSurfaceTractsDelegate, surfaceTract.number, undefined, props.sectionNumber, surfaceTract.id)}];

                    return (
                        <SurfaceTractListCard
                            key={surfaceTract.id.toString()}
                            surfaceTract={surfaceTract}
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                dispatch(
                                    push(
                                        `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${props.sectionNumber}/surface-tracts/${surfaceTract.number}`
                                    )
                                );
                            }}
                            actions={surfaceTractActions}
                        />
                    );
                })}
            </CardContainer>
        );
    };

    const createTract = async (tract: NewTract) => {
        let newTractInfo = await post(`${Url.tract.getTracts(appContext, props.sectionNumber, '')}/create-tract`, tract);

        if (response.ok) {
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${props.sectionNumber}/tracts/${newTractInfo.tractNumber}/horizons/1`
                )
            );
        }
    };

    const createEastTexasTract = async () => {
        await post(`${Url.project.getProject(appContext, appContext.projectNumber)}/tracts/create-east-texas-tract/`);

        if (response.ok) {
            let tractNumber = response.data?.tractNumber;
            dispatch(push(`/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/1/tracts/${tractNumber}/horizons/1`));
        }
    };

    const createEastTexasSurfaceTract = async () => {
        await post(`${Url.project.getProject(appContext, appContext.projectNumber)}/surface-tracts/create-east-texas-surface-tract/`);

        if (response.ok) {
            let tractNumber = response.data?.tractNumber;
            dispatch(push(`/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/1/surface-tracts/${tractNumber}`));
        }
    };

    const createTractAction = () => {
        if (props.landSystem !== LandSystem.EastTexas) {
            return () => dispatch(actions.layout.showDialog(DialogList.ADD_TRACT));
        } else {
            return () => createEastTexasTract();
        }
    };

    const createSurfaceTractAction = () => {
        if (props.landSystem !== LandSystem.EastTexas) {
            return () =>
                dialogContext.tract.createTract(
                    props.landSystem,
                    props.sectionNumber,
                    true,
                    counties.items,
                    'Surface',
                    () => {
                        onHideDialog(true);
                    },
                    null
                );
        } else {
            return () => createEastTexasSurfaceTract();
        }
    };

    const onConfirmDeleteTract = async () => {
        await request.delete(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${props.sectionNumber}/tracts/${selectedTractNumber}`
        );
        if (response.ok) {
            onHideDialog(true);
            setMineralTractToDelete(undefined);
            setMineralTractSurfaceOwnerships(undefined);
        } else {
            console.log('Unable to Delete Tract', selectedTractNumber);
        }
    };

    const onConfirmDeleteSurfaceTract = async () => {
        await request.delete(`${Url.tract.getSurfaceTracts(appContext, props.sectionNumber, '')}/${selectedSurfaceTractId}`);

        if (response.ok) {
            onHideDialog(true);
            setSurfaceTractToDelete(undefined);
        } else {
            console.log('Unable to Delete Surface Tract', selectedSurfaceTractId);
        }
    };

    const onHideDialog = (reset: boolean) => {
        setSelectedTractNumber(undefined);
        setSelectedSurfaceTractId(undefined);
        dispatch(actions.layout.hideDialog());
        if (reset && props.tractType === 'Surface Tract') {
            loadSurfaceTracts(tractListDataOptions.toString());
        } else if (reset) {
            loadTracts(tractListDataOptions.toString());
        }
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '1rem', background: '#ddd', padding: '.8rem' }}>
                <div style={{ height: '100%', verticalAlign: 'center', fontWeight: 'bold', fontSize: '1.3em' }}>
                    {props.tractType === 'Surface Tract' ? 'Surface Tracts' : 'Tracts'}
                </div>
                {props.tractType === 'Surface Tract' && (
                    <div>
                        <Button size="small" intent="primary" text="Create Surface Tract" onClick={createSurfaceTractAction()} />
                    </div>
                )}
                {props.tractType !== 'Surface Tract' && (
                    <div>
                        <Button size="small" intent="primary" text="Create Tract" onClick={createTractAction()} />
                    </div>
                )}
                {/* <div><Button size="small" intent="primary" text="Create Tract" onClick={createTract} /></div> */}
            </div>
            {props.landSystem === LandSystem.Jeffersonian && (
                <Filter
                    sortItems={[
                        { group: 1, text: 'Name', icon: 'alpha-up', orderName: 'TractName', orderDirection: 'ascending' },
                        { group: 1, text: 'Name Desc', icon: 'alpha-down', orderName: 'TractName', orderDirection: 'descending' },
                        { group: 1, text: 'Number', icon: 'numeric-up', orderName: 'Number', orderDirection: 'ascending' },
                        { group: 1, text: 'Number Desc', icon: 'numeric-down', orderName: 'Number', orderDirection: 'descending' },
                ]}
                    onFilterChanged={updateTractFilter}
                />
            )}
            {props.landSystem === LandSystem.EastTexas && (
                <Filter
                    sortItems={[
                        { group: 1, text: 'Tract Name', icon: 'alpha-up', orderName: 'tractName', orderDirection: 'ascending' },
                        { group: 1, text: 'Tract Number', icon: 'numeric-up', orderName: 'tractNumber', orderDirection: 'ascending' },
                    ]}
                    filterItems={[
                        { group: 1, text: 'Tract Name', orderName: 'tractName', orderDirection: 'ascending' },
                        { group: 1, text: 'Tract Number', orderName: 'tractNumber', orderDirection: 'ascending' },
                        { group: 1, text: 'Abstract Name', orderName: 'abstractName', orderDirection: 'ascending' },
                        { group: 1, text: 'Abstract Number', orderName: 'abstractNumber', orderDirection: 'ascending' },
                        { group: 1, text: 'Tag', orderName: 'tag', orderDirection: 'ascending' },
                    ]}
                    onFilterChanged={updateTractFilter}
                />
            )}
            {props.tractType === 'Surface Tract' && surfaceTractList()}
            {props.tractType !== 'Surface Tract' && tractsList()}

            <AddTract
                sectionCounties={counties}
                createTract={createTract}
                hideDialog={() => dispatch(actions.layout.hideDialog())}
                sectionNumber={props.landSystem === LandSystem.Eastern ? undefined : props.sectionNumber}
                mapNumber={props.landSystem === LandSystem.Eastern ? props.sectionNumber : undefined}
            />
            <Dialog dialogName={DialogList.CONFIRM_TRACT_DELETE} title="Confirm Delete Tract">
                <div>Are you sure that you want to permanently delete the selected tract?</div>
                <div className="mt-2 button-row">
                    <Button
                        text="OK"
                        onClick={props.tractType === 'Surface Tract' ? onConfirmDeleteSurfaceTract : onConfirmDeleteTract}
                        intent="primary"
                    />
                    <Button text="Cancel" onClick={() => onHideDialog(false)} />
                </div>
            </Dialog>
        </>
    );
};

export default TractList;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/TractList.tsx